import React, { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Button,
  Chip,
  alpha,
  Zoom,
  Fade
} from '@mui/material';
import FlightIcon from '@mui/icons-material/Flight';
import TrainIcon from '@mui/icons-material/Train';
import HotelIcon from '@mui/icons-material/Hotel';
import ShieldIcon from '@mui/icons-material/Shield';
import ExploreIcon from '@mui/icons-material/Explore';
import PublicIcon from '@mui/icons-material/Public';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
// import API from '../api/axios'; // Commented out API import
import FlightBookingPopup from '../components/FlightBookingPopup';
import RailwayBookingPopup from '../components/RailwayBookingPopup';
import HotelBookingPopup from '../components/HotelBookingPopup';

const iconMap = {
  'Flight': <FlightIcon sx={{ fontSize: 40 }} />,
  'FlightTakeoff': <FlightIcon sx={{ fontSize: 40 }} />,
  'Train': <TrainIcon sx={{ fontSize: 40 }} />,
  'Hotel': <HotelIcon sx={{ fontSize: 40 }} />,
  'Shield': <ShieldIcon sx={{ fontSize: 40 }} />,
  'Explore': <ExploreIcon sx={{ fontSize: 40 }} />,
  'TravelExplore': <PublicIcon sx={{ fontSize: 40 }} />
};

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false); // Changed to false since we're not fetching
  const [error, setError] = useState(null);
  const [flightPopupOpen, setFlightPopupOpen] = useState(false);
  const [railwayPopupOpen, setRailwayPopupOpen] = useState(false);
  const [hotelPopupOpen, setHotelPopupOpen] = useState(false);
  const navigate = useNavigate();

  // useEffect(() => {
  //   fetchServices();
  // }, []);

  // const fetchServices = async () => {
  //   try {
  //     const { data } = await API.get('/services');
  //     setServices(data);
  //   } catch (error) {
  //     setError('Failed to load services');
  //     console.error('Error fetching services:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} thickness={4} sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
      </Container>
    );
  }

  // Default services if none from DB
  const defaultServices = [
    {
      name: 'International Tour Packages',
      description: 'Explore exotic destinations worldwide with our curated international tour packages. From European adventures to Asian explorations, we offer comprehensive packages including flights, accommodation, and guided tours.',
      icon: 'Flight'
    },
    {
      name: 'Domestic Tour Packages',
      description: 'Discover the incredible diversity of India with our domestic tour packages. Visit ancient temples, pristine beaches, majestic mountains, and vibrant cities with expertly planned itineraries.',
      icon: 'Explore'
    },
    {
      name: 'Flight Booking',
      description: 'Book domestic and international flights at the most competitive prices. We partner with major airlines to offer you the best deals, flexible booking options, and hassle-free reservations.',
      icon: 'FlightTakeoff'
    },
    {
      name: 'Indian Railway Booking',
      description: 'Experience the vast Indian railway network with our easy booking service. From luxury trains like Palace on Wheels to regular express trains, we handle all your railway travel needs.',
      icon: 'Train'
    },
    {
      name: 'Hotel Booking',
      description: 'Find your perfect stay from our extensive network of hotels worldwide. From budget-friendly options to luxury resorts, we ensure comfortable accommodation that suits your preferences and budget.',
      icon: 'Hotel'
    },
    {
      name: 'Travel Insurance',
      description: 'Travel with peace of mind with our comprehensive travel insurance plans. Coverage includes medical emergencies, trip cancellations, lost baggage, and more to protect you during your journey.',
      icon: 'Shield'
    }
  ];

  const displayServices = defaultServices; // Always use default services now

  const handleServiceClick = (serviceName) => {
    if (serviceName === 'International Tour Packages') {
      navigate('/tours?category=International');
    } else if (serviceName === 'Domestic Tour Packages') {
      navigate('/tours?category=Domestic');
    } else if (serviceName === 'Travel Insurance') {
      navigate('/contact');
    } else if (serviceName === 'Flight Booking') {
      setFlightPopupOpen(true);
    } else if (serviceName === 'Indian Railway Booking') {
      setRailwayPopupOpen(true);
    } else if (serviceName === 'Hotel Booking') {
      setHotelPopupOpen(true);
    }
  };

  return (
    <Box sx={{ bgcolor: '#fafafa', minHeight: '100vh', pb: 10 }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '55vh',
          minHeight: 450,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url("https://images.unsplash.com/photo-1436491865332-7a61a109bb05?auto=format&fit=crop&w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            transform: 'scale(1.05)',
            transition: 'transform 10s ease-out',
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(99, 102, 241, 0.75) 100%)',
            zIndex: 1,
          },
          '&:hover::before': {
            transform: 'scale(1.1)',
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <Fade in={true} timeout={800}>
            <Box>
              <Box sx={{ mb: 3, display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', px: 2, py: 0.8, borderRadius: 50, border: '1px solid rgba(255,255,255,0.2)' }}>
                <AutoAwesomeIcon sx={{ fontSize: 18, color: '#fbbf24' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
                  What We Offer
                </Typography>
              </Box>
              <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 800, textShadow: '0 4px 20px rgba(0,0,0,0.3)', mb: 2 }}>
                Our Services
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.95, maxWidth: 650, mx: 'auto', lineHeight: 1.7 }}>
                From booking flights to securing luxury stays and tailor-made packages, we offer comprehensive solutions for every step of your journey.
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -6, position: 'relative', zIndex: 3 }}>
        <Grid container spacing={4}>
          {displayServices.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                <Card 
                  elevation={0}
                  onClick={() => handleServiceClick(service.name)}
                  sx={{ 
                    cursor: 'pointer',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 4,
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.8)',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    position: 'relative',
                    overflow: 'visible',
                    '&:hover': {
                      boxShadow: '0 20px 50px rgba(99, 102, 241, 0.15)',
                      '&::before': { opacity: 1 },
                      '& .icon-wrapper': {
                        transform: 'scale(1.1) rotate(5deg)',
                        boxShadow: '0 10px 20px rgba(99, 102, 241, 0.2)'
                      }
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 4, pt: 5 }}>
                    <Box 
                      className="icon-wrapper"
                      sx={{ 
                        width: 72, height: 72,
                        borderRadius: 4,
                        bgcolor: alpha('#6366f1', 0.1),
                        color: '#6366f1',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 3,
                        transition: 'all 0.4s ease'
                      }}
                    >
                      {iconMap[service.icon] || <ExploreIcon sx={{ fontSize: 36 }} />}
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, color: '#1e293b' }}>
                      {service.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.7, mb: 3 }}>
                      {service.description}
                    </Typography>
                    <Chip 
                      label="Available" 
                      size="small" 
                      sx={{ 
                        bgcolor: alpha('#10b981', 0.1), 
                        color: '#059669', 
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: 1
                      }}
                    />
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Box 
          sx={{ 
            textAlign: 'center', 
            mt: 10, 
            p: { xs: 5, md: 8 }, 
            borderRadius: 6, 
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 20px 50px rgba(99, 102, 241, 0.3)',
            background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)'
          }}
        >
          {/* Decorative shapes */}
          <Box sx={{ position: 'absolute', top: -50, left: -50, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)', filter: 'blur(30px)' }} />
          <Box sx={{ position: 'absolute', bottom: -50, right: -50, width: 300, height: 300, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)', filter: 'blur(40px)' }} />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 800, mb: 2 }}>
              Ready to Start Your Journey?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, fontWeight: 400, opacity: 0.9, maxWidth: 600, mx: 'auto' }}>
              Let our experts handle the logistics while you focus on creating memories. Contact us today for personalized travel planning.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/contact')}
              sx={{ 
                bgcolor: 'white', 
                color: '#0C2340',
                borderRadius: 50,
                px: 5,
                py: 1.8,
                fontWeight: 800,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                '&:hover': { 
                  bgcolor: '#f8fafc',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.2)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Get in Touch
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Service Booking Popups */}
      <FlightBookingPopup
        open={flightPopupOpen}
        onClose={() => setFlightPopupOpen(false)}
      />
      <RailwayBookingPopup
        open={railwayPopupOpen}
        onClose={() => setRailwayPopupOpen(false)}
      />
      <HotelBookingPopup
        open={hotelPopupOpen}
        onClose={() => setHotelPopupOpen(false)}
      />
    </Box>
  );
};

export default Services;
